
import { TEXTURE_ART, elem } from "./chrome";

export const LEFT_MOUSE_WINDOW = {
  width: 32,
  height: 32 * 3,
  gapAboveBar: 5,
} as const;

export const RIGHT_MOUSE_WINDOW = {
  width: 32 * 2,
  height: 32,
  gapAboveBar: 5,
} as const;

export const LEFT_MOUSE_BUTTONS = [
  "button_move_and_attack",
  "button_auto_attack",
  "button_camera",
] as const;

export const RIGHT_MOUSE_BUTTONS = [
  "button_move_and_attack",
  "button_camera",
  "button_skill",
] as const;

/** The three faces each mouse-mode button has, by the name its layout gives
 *  them: attack (`mousebuttonwindow.py:22-24`, `rightmousebuttonwindow.py:22-24`),
 *  auto attack (`mousebuttonwindow.py:36-38`), camera
 *  (`mousebuttonwindow.py:50-52`) and skill (`rightmousebuttonwindow.py:50-52`).
 *  `button_move_and_attack` is `Mouse_Button_Attack_*`, not `Mouse_Button_Move_*`:
 *  the latter is the pair of buttons on the bar itself
 *  (`locale/en/ui/taskbar.py:293-295`). */
export const MOUSE_BUTTON_SPRITES = {
  attack: "mouse_button_attack",
  auto: "mouse_button_auto_attack",
  camera: "mouse_button_camera",
  moveAndAttack: "mouse_button_attack",
  skill: "mouse_button_skill",
} as const;

/** One face of one taskbar sprite, by the `.sub` the layout names. */
export function taskbarSprite(name: string, face: 1 | 2 | 3): string {
  return `${TEXTURE_ART}/game/taskbar/${name}_0${String(face)}.png`;
}

export type MouseButtonEvent = "move_and_attack" | "auto" | "camera" | "skill";

export interface MouseModeWindows {
  readonly left: HTMLElement;
  readonly right: HTMLElement;
  toggleLeft(): void;
  toggleRight(): void;
  hide(): void;
}

const LABELS: Record<MouseButtonEvent, string> = {
  move_and_attack: "Attack",
  auto: "Auto Attack",
  camera: "Camera",
  skill: "Skill",
};

export function attachMouseModeWindows(
  taskbar: HTMLElement,
  onSelect: (hand: "left" | "right", event: MouseButtonEvent) => void,
): MouseModeWindows {
  const doc = taskbar.ownerDocument ?? document;
  const left = buildWindow(doc, "left", [...LEFT_MOUSE_BUTTONS], (event) => {
    onSelect("left", event);
    left.hidden = true;
  });
  left.style.width = `${LEFT_MOUSE_WINDOW.width}px`;
  left.style.height = `${LEFT_MOUSE_WINDOW.height}px`;
  left.style.left = "calc(50% - 128px)";
  left.style.top = `${3 - LEFT_MOUSE_WINDOW.height - LEFT_MOUSE_WINDOW.gapAboveBar}px`;

  const right = buildWindow(doc, "right", [...RIGHT_MOUSE_BUTTONS], (event) => {
    onSelect("right", event);
    right.hidden = true;
  });
  right.style.width = `${RIGHT_MOUSE_WINDOW.width}px`;
  right.style.height = `${RIGHT_MOUSE_WINDOW.height}px`;
  right.style.left = `calc(50% + 205px - ${RIGHT_MOUSE_WINDOW.width - 32}px)`;
  right.style.top = `${3 - RIGHT_MOUSE_WINDOW.height - RIGHT_MOUSE_WINDOW.gapAboveBar}px`;
  const skill = right.querySelector<HTMLElement>('[data-mouse-event="skill"]');
  if (skill !== null) skill.hidden = true;

  taskbar.append(left, right);
  return {
    left,
    right,
    toggleLeft() {
      left.hidden = !left.hidden;
      if (!left.hidden) right.hidden = true;
    },
    toggleRight() {
      right.hidden = !right.hidden;
      if (!right.hidden) left.hidden = true;
    },
    hide() {
      left.hidden = true;
      right.hidden = true;
    },
  };
}

function buildWindow(
  doc: Document,
  hand: "left" | "right",
  names: readonly string[],
  onSelect: (event: MouseButtonEvent) => void,
): HTMLElement {
  const root = elem(doc, "div", `legacy-mouse-mode legacy-mouse-mode-${hand}`);
  root.dataset.window = hand === "left" ? "ButtonWindow" : "RightButtonWindow";
  root.hidden = true;
  for (const name of names) {
    const event = eventFor(name);
    const btn = doc.createElement("button");
    btn.type = "button";
    btn.className = "legacy-mouse-mode-button";
    btn.dataset.name = name;
    btn.dataset.mouseEvent = event;
    btn.setAttribute("aria-label", LABELS[event]);
    btn.title = LABELS[event];
    const sprite = spriteFor(event);
    btn.style.backgroundImage = `url("${taskbarSprite(sprite, 1)}")`;
    btn.style.setProperty("--mouse-over", `url("${taskbarSprite(sprite, 2)}")`);
    btn.style.setProperty("--mouse-down", `url("${taskbarSprite(sprite, 3)}")`);
    btn.addEventListener("click", (click) => {
      click.stopPropagation();
      onSelect(event);
    });
    root.append(btn);
  }
  return root;
}

function eventFor(name: string): MouseButtonEvent {
  if (name === "button_auto_attack") return "auto";
  if (name === "button_camera") return "camera";
  if (name === "button_skill") return "skill";
  return "move_and_attack";
}

function spriteFor(event: MouseButtonEvent): string {
  if (event === "auto") return MOUSE_BUTTON_SPRITES.auto;
  if (event === "camera") return MOUSE_BUTTON_SPRITES.camera;
  if (event === "skill") return MOUSE_BUTTON_SPRITES.skill;
  return MOUSE_BUTTON_SPRITES.moveAndAttack;
}
